// ---------------------------------------------------------------------------
// useReviewModal — custom hook for review modal data fetching & mutations
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api-client';
import type { ApiError } from '@/lib/api-client';
import { toast } from '@/utils/toast';
import type {
  PaymentDetail,
  HoldRequest,
  DeclineRequest,
  CancelRequest,
  CommentRequest,
} from './types';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const paymentKey = (id: string) => ['payment', id] as const;

// Stable disabled key — never collides with real payment IDs
const DISABLED_KEY = ['payment', '__disabled__'] as const;

// ---------------------------------------------------------------------------
// Error helper
// ---------------------------------------------------------------------------

function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    (typeof (err as ApiError).message === 'string' || Array.isArray((err as ApiError).message))
  );
}

function extractErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    return Array.isArray(err.message) ? err.message.join(', ') : err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'An unexpected error occurred';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseReviewModalReturn {
  payment: PaymentDetail | undefined;
  isLoading: boolean;
  error: Error | null;
  approvePayment: () => void;
  holdPayment: (body: HoldRequest) => void;
  declinePayment: (body: DeclineRequest) => void;
  cancelPayment: (body: CancelRequest) => void;
  addComment: (body: CommentRequest) => Promise<unknown>;
  uploadDocument: (file: File) => void;
  isApproving: boolean;
  isHolding: boolean;
  isDeclining: boolean;
  isCancelling: boolean;
  isCommenting: boolean;
  isUploading: boolean;
}

export function useReviewModal(paymentId: string | null): UseReviewModalReturn {
  const queryClient = useQueryClient();
  const enabled = paymentId !== null;

  const paymentQuery = useQuery<PaymentDetail>({
    queryKey: paymentId ? paymentKey(paymentId) : DISABLED_KEY,
    queryFn: () => api.get<PaymentDetail>(`/api/payments/${paymentId}`),
    enabled,
  });

  const invalidateAll = () => {
    if (!paymentId) return;
    void queryClient.invalidateQueries({ queryKey: paymentKey(paymentId) });
    void queryClient.invalidateQueries({ queryKey: ['payments'] });
  };

  const approve = useMutation({
    mutationFn: () => api.post(`/api/payments/${paymentId}/approve`, {}),
    onSuccess: () => {
      toast.success('Payment approved successfully');
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const hold = useMutation({
    mutationFn: (body: HoldRequest) => api.post(`/api/payments/${paymentId}/hold`, body),
    onSuccess: () => {
      toast.success('Payment placed on hold');
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const decline = useMutation({
    mutationFn: (body: DeclineRequest) => api.post(`/api/payments/${paymentId}/reject`, body),
    onSuccess: () => {
      toast.success('Payment declined');
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const cancel = useMutation({
    mutationFn: (body: CancelRequest) => api.post(`/api/payments/${paymentId}/cancel`, body),
    onSuccess: () => {
      toast.success('Payment cancelled');
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const comment = useMutation({
    mutationFn: (body: CommentRequest) => api.post(`/api/payments/${paymentId}/comment`, body),
    onSuccess: () => {
      toast.success('Comment added');
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/api/payments/${paymentId}/document`, formData);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Document uploaded successfully');
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err));
    },
  });

  return {
    payment: paymentQuery.data,
    isLoading: paymentQuery.isLoading,
    error: paymentQuery.error,
    approvePayment: () => approve.mutate(),
    holdPayment: hold.mutate,
    declinePayment: decline.mutate,
    cancelPayment: cancel.mutate,
    addComment: comment.mutateAsync,
    uploadDocument: upload.mutate,
    isApproving: approve.isPending,
    isHolding: hold.isPending,
    isDeclining: decline.isPending,
    isCancelling: cancel.isPending,
    isCommenting: comment.isPending,
    isUploading: upload.isPending,
  };
}
